import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { Stack } from 'expo-router';

import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';

// TODO(privacy-policy): the same text is served publicly by the Expo web build
// at /privacy-policy — paste that URL into Play Console → App content.
// Contact is andylearnsai+fimfam@gmail.com for v1 (a live Gmail alias);
// swap to a branded privacy@fimfam.app mailbox later if desired.

/**
 * Privacy Policy Screen
 * Discloses what user data FimFam-AI collects, why, and which sub-processors
 * receive it. Required by Google Play User Data Policy.
 */
export default function PrivacyPolicyScreen() {
  const colorScheme = useColorScheme();
  const backgroundColor = Colors[colorScheme ?? 'light'].background;
  const textColor = Colors[colorScheme ?? 'light'].text;
  const accentColor = Colors[colorScheme ?? 'light'].tint;

  return (
    <>
      <Stack.Screen
        options={{
          title: 'Privacy Policy',
          headerShown: true,
        }}
      />
      <ScrollView
        style={[styles.container, { backgroundColor }]}
        contentContainerStyle={styles.content}>
        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>1. What We Collect</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            FimFam-AI collects the following data to provide the service:
            email address, calendar events (including titles, times, locations,
            and attendees you choose to include), voice recordings you submit
            for transcription, images you attach to chat messages, coarse
            location (city level, for weather), family member names and notes
            you enter, your home address if you choose to add one (used as the
            start and end point when planning day routes), your profile
            picture, locale, and country.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>2. Why We Collect It</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            Your data powers the in-app AI assistant, calendar summaries, and
            reminders. We do not sell your personal data and do not use it for
            advertising.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>3. Sub-Processors</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            We share the minimum data necessary with the following third
            parties to operate the service:
          </Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            • Supabase (United States) — authentication and database.{'\n'}
            • OpenAI (United States) — AI model inference (gpt-5.4-mini),
            voice transcription (Whisper), and image understanding (vision).
            Route planning sends your home address and the day&apos;s stops to
            OpenAI to build the route. Inputs are not used by OpenAI to train
            its models.{'\n'}
            • Google — Sign-In only (used to verify your identity). We do
            not store Google account credentials and do not access your
            Google Calendar from our servers.{'\n'}
            • Resend — transactional email (account-deletion confirmations).{'\n'}
            • Open-Meteo — anonymous weather lookups by approximate location.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>4. How We Protect Your Data</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            All network traffic is encrypted in transit (TLS), and data at rest
            is encrypted by our hosting provider. We do not store any third-party
            account credentials or OAuth tokens on our servers.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>5. Retention and Deletion</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            You can permanently delete your FimFam-AI account from
            Settings → Your Account → Delete Account. Deletion removes your
            profile, preferences, family notes, and any data we store about
            you. Calendar events in your native calendar (e.g. Google
            Calendar) are NOT deleted — FimFam only reads from those calendars
            and never owns the events.
          </Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            If you have uninstalled the app, you can request deletion by
            emailing andylearnsai+fimfam@gmail.com from the address tied to
            your account.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>6. Children</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            FimFam-AI is not directed to children under 13 and we do not
            knowingly collect personal information from them. If you believe a
            child under 13 has provided us with personal data, contact us and
            we will delete it.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>7. Your Rights</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            You can access, correct, or delete your data at any time through
            the in-app settings, or by contacting us at the address below.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>8. Contact</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            Questions about this policy or your data: andylearnsai+fimfam@gmail.com.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.heading, { color: accentColor }]}>9. Changes</Text>
          <Text style={[styles.paragraph, { color: textColor }]}>
            We may update this policy as the service evolves. Material changes
            will be communicated in-app before they take effect.
          </Text>
        </View>

        <View style={[styles.footer, { borderTopColor: '#e0e0e0' }]}>
          <Text style={[styles.footerText, { color: textColor }]}>
            Last Updated: July 2026
          </Text>
          <Text style={[styles.footerText, { color: textColor }]}>FimFam-AI</Text>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 20,
    paddingVertical: 24,
  },
  section: {
    marginBottom: 24,
  },
  heading: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 10,
  },
  paragraph: {
    fontSize: 14,
    lineHeight: 20,
    opacity: 0.85,
    marginBottom: 8,
  },
  footer: {
    marginTop: 32,
    paddingTop: 20,
    borderTopWidth: 1,
    gap: 8,
  },
  footerText: {
    fontSize: 12,
    opacity: 0.5,
    textAlign: 'center',
  },
});
